import { Link } from 'react-router-dom';

import { Button } from '@ibas/ui';

import {
  CustomCursor,
  Magnetic,
  NavyAtmosphere,
  ScrollProgress,
  ScrollReveal,
  SplitReveal,
} from '@/components/motion';
import {
  AccreditationLogos,
  AchievementBand,
  CampusSlider,
  CinematicHero,
  ConstellationHero,
  EnquiryForm,
  ExperienceHorizontal,
  FeatureSticky,
  GraduatesShowcase,
  HeroGallery,
  MediaMosaic,
  ProgrammeShowcase,
  ProofStats,
  SerenityIntro,
  StudentVideos,
  WordHero,
} from '@/components/sections';
import { SiteFooter } from '@/components/layout';
import { JourneyRoadmap, MegaNav, WaveDivider } from '@/components/ui';
import { brand } from '@/config/brand';

/**
 * IBAS home page — a premium, motion-led composition. Every section responds to
 * scroll and/or the pointer (per the ibas-motion skill). The story: arrive →
 * proof → recognition → life → programmes → why → experience → difference →
 * gallery → voices → journey → community → statement → vision → act.
 */
export function HomePage() {
  return (
    <div className="min-h-dvh bg-background">
      {/* Global interactive touches */}
      <ScrollProgress />
      <CustomCursor />

      <MegaNav />

      <main>
        {/* Hero */}
        <CinematicHero id="top" />

        {/* Proof + recognition */}
        <ProofStats />
        <AccreditationLogos />

        {/* ACBSP student achievement (accreditation-mandated public disclosure) */}
        <AchievementBand id="student-achievement" />

        {/* Life at IBAS (interactive media) */}
        <MediaMosaic />

        {/* Programmes + why */}
        <ProgrammeShowcase id="programmes" />
        <FeatureSticky />

        {/* The experience (pinned horizontal scroll) */}
        <ExperienceHorizontal />

        {/* Campus Thalwil */}
        <CampusSlider />

        {/* Difference + gallery */}
        <WaveDivider />
        <HeroGallery />

        {/* Voices + graduates + journey */}
        <StudentVideos />
        <GraduatesShowcase />
        <JourneyRoadmap />

        {/* Global community (relocated constellation) + statement */}
        <ConstellationHero />
        <WordHero />

        {/* Vision */}
        <SerenityIntro />

        {/* Closing call to action — centered full screen */}
        <section className="relative isolate flex min-h-[100svh] items-center overflow-hidden bg-brand-navy">
          {/* Shared decorative backdrop — see NavyAtmosphere; matches WordHero
              so the two plain-navy "statement" screens read as a pair. */}
          <NavyAtmosphere />

          <ScrollReveal className="relative z-10 mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 py-20 text-center">
            <p className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
              {brand.campaignLine}
            </p>
            <SplitReveal
              as="h2"
              by="words"
              className="font-display text-4xl font-semibold text-white sm:text-5xl"
            >
              Ready to graduate with Swiss quality?
            </SplitReveal>
            <p className="max-w-xl text-balance text-white/70">{brand.essence}</p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <Magnetic strength={0.4} radius={140}>
                {/* Gold primary action — a navy (primary) button would vanish
                    into this navy section. Gold bg + navy text per brand. */}
                <Button asChild size="lg" variant="secondary" data-cursor>
                  <Link to="/apply">Start your application</Link>
                </Button>
              </Magnetic>
              {/* Transparent outline on navy: the default `outline` variant is
                  bg-background (white), which with white text rendered an
                  invisible white-on-white button — force transparent + white. */}
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/admissions#find-advisor">Connect with an adviser</Link>
              </Button>
            </div>
          </ScrollReveal>
        </section>

        {/* Enquiry — final call before the footer */}
        <EnquiryForm />
      </main>

      <SiteFooter />
    </div>
  );
}
